import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import { snmpDestTemplate } from 'routes/SnmpDestination/templates/snmpDestTemplate';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptSnmpDestTemplate
} from 'routes/SnmpDestination/utils/snmpDestTemplate.adapter';
import { newSnmpDest } from 'routes/SnmpDestination/SnmpDestination.types';
import { SnmpDestination, SnmpDestinationUI } from 'data/snmp/snmp.types';
import * as snmpDestActions from 'data/snmp/snmp.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/snmp/snmp.selectors';
import SnmpDestForm, {
  Ref
} from 'routes/SnmpDestination/components/SnmpDestinationForm';
import { FieldUpdateHandlerParam } from 'shared/form';
import { adaptSnmpDestPayload } from 'data/snmp/snmp.adapter';
import { getAuditDetailsToSubmit } from 'routes/SnmpDestination/utils/snmpDestTemplateDataToSubmit';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  snmpDestTemplateDef: adaptTemplateVisibilityMode(snmpDestTemplate, 'create'),
  snmpUsers: selectors.getSnmpUsers(state)
});

const mapDispatch = {
  goToSmnpDestList: snmpDestActions.goToSmnpDestList,
  createSnmpDest: snmpDestActions.createSnmpDest,
  getSnmpUsers: snmpDestActions.getSnmpUsers
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const SnmpDestCreate = ({
  history,
  userId,
  deliveryFunctionId,
  snmpDestTemplateDef,
  userName,
  isAuditEnabled,
  goToSmnpDestList,
  createSnmpDest,
  getSnmpUsers,
  snmpUsers
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<SnmpDestination>(
    cloneDeep(newSnmpDest)
  );
  const [template, setTemplate] = useState(snmpDestTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getSnmpUsers(deliveryFunctionId);
  }, [deliveryFunctionId, getSnmpUsers]);

  useEffect(() => {
    if (!template) {
      return;
    }
    let clonedTemplate = cloneDeep(template);
    clonedTemplate = adaptTemplateOptions(
      clonedTemplate,
      snmpUsers,
    );
    setTemplate(clonedTemplate);
  }, [snmpUsers]);

  const handleFieldUpdate = ({
    name: fieldName,
    value
  }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as SnmpDestination);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value
    };

    clonedTemplate = adaptSnmpDestTemplate(clonedTemplate, fieldName, value);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const handleClickSave = () => {
    const payload = getSnmpDestPayload();
    if (payload) {
      createSnmpDest(deliveryFunctionId, payload, history);
    }
  };

  const getSnmpDestPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const snmpDestFormData: SnmpDestinationUI = formRef.current.getValues();

    const payload = {
      data: adaptSnmpDestPayload(snmpDestFormData),
      auditDetails: getAuditDetailsToSubmit(
        snmpDestFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE'
      )
    };
    return payload;
  };

  const handleCancel = () => goToSmnpDestList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <SnmpDestForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="SnmpDestCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('snmpDest.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SnmpDestCreate));
